import { Component, OnInit } from '@angular/core';
import { TableAction, TableColumn } from '../../shared/models/table-models';
import { Table } from '../../shared/table/table';

import { Router } from '@angular/router';
import { Setup } from '../../setup/shared/service/setup';
import { Popup } from '../../../core/shared/components/popup/popup';
import { DynamicFieldConfig } from '../../../core/shared/dynamic-form/form-config.model';
import { BehaviorSubject } from 'rxjs';
import { OnboradingService } from '../shared/service/onboarding.service';
import { PickListService } from '../../../core/shared/services/picklist.service';
import { PKL_GRP } from '../../../core/shared/models/picklist.model';
import { filter, take } from 'rxjs/operators';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ToastService } from '../../../core/shared/services/toast.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

interface Client {
  id: number;
  onboardingSeq: number | null;
  clientName: string;
  contactName: string;
  phone: string;
  email: string;
  source: string;
  status: number;
  assignedTo: string;
  createdBy: string;
  createdDate?: string;
  lastUpdatedDate?: string;
}

@Component({
  selector: 'app-client-list',
  imports: [Table, Popup, CommonModule, FormsModule, TranslatePipe],
  templateUrl: './client-list.html',
  styleUrl: './client-list.css',
})
export class ClientList implements OnInit {
  showClientActionPopup = false;

  pendingActionClient: Client | null = null;

  selectedClientAction: 'reject' | 'sendBackToRO' = 'sendBackToRO';
  statusMap: Record<number, string> = {};
  isClientActionSubmitting = false;
  clients: Client[] = [];
  clientOptions: any[] = [];
  clientOptions$ = new BehaviorSubject<any[]>([]);
  formData: any = {};
  showPopup = false;
  actions: TableAction[] = [];

  clientFormFields: DynamicFieldConfig[] = [];
  columns: TableColumn[] = [];

  constructor(
    private clientService: Setup,
    private onboarding: OnboradingService,
    private picklistService: PickListService,
    private router: Router,
    private toastr: ToastService,
    private translate: TranslateService,
  ) {}

  ngOnInit(): void {
    this.setActions();

    this.loadPicklists();
    this.loadClientOptions();

    this.clientFormFields = [
      {
        type: 'select',
        key: 'clientId',
        label: 'ONB_CLIENT_LIST.INSTITUTE_LIST',
        searchable: true,
        options$: this.clientOptions$,
        validations: { required: true },
      },
    ];
  }
  private readonly statusGroups = [
    PKL_GRP.LEADS_STATUS, // 15
    PKL_GRP.ONBOARDING_STATUS, // 23
    PKL_GRP.PANDDING_STATUS, // 26
  ];

  loadPicklists(): void {
    this.picklistService.loadCodes(this.statusGroups);

    this.picklistService.picklists$
      .pipe(
        filter((picklists: any) => {
          return this.statusGroups.some((group) => {
            const directOptions = picklists?.[String(group)] ?? picklists?.[group];

            return Array.isArray(directOptions) && directOptions.length > 0;
          });
        }),
        take(1),
      )
      .subscribe((picklists: any) => {
        this.statusMap = this.getAllClientStatusMap(picklists);

        console.log('Final status map:', this.statusMap);

        this.columns = this.buildColumns();
        this.loadClients();
      });
  }
  private getAllClientStatusMap(picklists?: Record<string, any[]>): Record<number, string> {
    const map: Record<number, string> = {
      0: 'ONB_CLIENT_LIST.NOT_STARTED',
    };

    this.statusGroups.forEach((group) => {
      const directOptions = picklists?.[String(group)] ?? picklists?.[group];

      const options = Array.isArray(directOptions)
        ? directOptions
        : this.picklistService.getOptionsSync(group) || [];

      options.forEach((option: any) => {
        const label = option?.label ?? option?.refCdDscr ?? option?.original?.refCdDscr ?? '';

        const statusKeys = [
          option?.value,
          option?.refCdSeq,
          option?.refCd,
          option?.original?.refCdSeq,
          option?.original?.refCd,
        ];

        statusKeys.forEach((key) => {
          if (key === null || key === undefined || String(key).trim() === '' || !label) {
            return;
          }

          const numericKey = Number(key);

          if (!Number.isNaN(numericKey)) {
            map[numericKey] = String(label);
          }
        });
      });
    });

    return map;
  }

  // ================= LOAD CLIENTS =================
  loadClients(): void {
    this.onboarding.getClntAprvlLeads().subscribe({
      next: (res: any) => {
        const maybeData = res?.data ?? res;

        let raw: any[] = [];

        if (Array.isArray(maybeData)) {
          raw = maybeData;
        } else if (maybeData && typeof maybeData === 'object') {
          if (Array.isArray(maybeData.data)) raw = maybeData.data;
          else if (Array.isArray(maybeData.items)) raw = maybeData.items;
          else if (Array.isArray(maybeData.leads)) raw = maybeData.leads;
          else {
            const firstArray = Object.values(maybeData).find((v) => Array.isArray(v));
            if (Array.isArray(firstArray)) raw = firstArray;
          }
        }

        if (!Array.isArray(raw)) raw = [];

        this.clients = raw.map(
          (item: any): Client => ({
            id: Number(item.clntSeq ?? item.leadSeq ?? 0),

            onboardingSeq:
              item.crptOnbrdngSeq !== null && item.crptOnbrdngSeq !== undefined
                ? Number(item.crptOnbrdngSeq)
                : null,

            clientName: item.nmOfInstitution ?? item.clntNm ?? '',
            contactName: item.cntctPrsnNm ?? item.cntctNm ?? '',

            phone: String(item.mblNum ?? item.cntctNum ?? ''),

            email: item.emlAddrss ?? item.cntctEmail ?? '',

            source: item.leadSrc ?? '',

            status: Number(item.stsKey ?? 0),

            assignedTo: item.pendingWithNm ?? '',
            createdBy: item.crtdBy ?? '',

            createdDate: item.crtdDt,
            lastUpdatedDate: item.lastUpdDt,
          }),
        );
        // .sort(
        //   (a, b) =>
        //     new Date(b.lastUpdatedDate || 0).getTime() -
        //     new Date(a.lastUpdatedDate || 0).getTime(),
        // );

        this.clients.forEach((client) => {
          if (!this.statusMap[client.status]) {
            this.statusMap[client.status] = this.translate.instant('ONB_CLIENT_LIST.UNKNOWN_STATUS', {
              status: client.status,
            });
          }
        });

        this.columns = this.buildColumns();

        console.log('Mapped clients:', this.clients);
        console.log('Updated status map:', this.statusMap);

        console.log('clients', this.clients);
      },
      error: () => {
        this.clients = [];
      },
    });
  }

  // ================= TABLE =================
  buildColumns(): TableColumn[] {
    return [
      { key: 'clientName', label: 'ONB_CLIENT_LIST.INSTITUTE_NAME' },
      { key: 'contactName', label: 'ONB_CLIENT_LIST.CONTACT_NAME' },
      { key: 'phone', label: 'ONB_CLIENT_LIST.PHONE' },
      { key: 'email', label: 'ONB_CLIENT_LIST.EMAIL' },
      { key: 'assignedTo', label: 'ONB_CLIENT_LIST.ASSIGNED_TO' },
      { key: 'createdBy', label: 'COMMON.CREATED_BY' },
      {
        key: 'status',
        label: 'COMMON.STATUS',
        type: 'map',
        map: this.statusMap,
      },
    ];
  }

  // ================= DROPDOWN =================
  loadClientOptions(): void {
    this.clientService.getclientList().subscribe((data: any[]) => {
      const options = data
        .map((item) => ({
          label: item.clntNm?.trim() ?? '',
          value: item,
        }))
        .sort((a, b) => a.label.localeCompare(b.label, undefined, { sensitivity: 'base' }));

      this.clientOptions = options;
      this.clientOptions$.next(options);
    });
  }

  // ================= POPUP =================
  openPopup(): void {
    this.formData = {};
    this.showPopup = true;
  }

  handleSave = (data: any) => {
    const selectedClient = data.clientId;

    console.log('selected:', selectedClient.clientSeq, selectedClient.clntNm);

    this.showPopup = false;

    this.router.navigate(['/onboarding/corporate', selectedClient.clientSeq], {
      queryParams: { clientId: selectedClient.clientSeq },
    });
  };

  // ================= ACTIONS =================
  // actions: TableAction[] = [
  //   {
  //     icon: 'images/edit-icon.png',
  //     class: 'edit',
  //     onClick: (row) => this.editClient(row),
  //     iconSize: 'w-6 h-6',
  //   },
  //   {
  //     icon: 'images/view-more.png',
  //     class: 'view',
  //     onClick: (row) => this.viewClient(row),
  //     iconSize: 'w-6 h-6',
  //   },
  //   {
  //     icon: 'images/curncy-icon.png',
  //     class: 'money',
  //     onClick: (row) => this.openExposure(row),
  //     iconSize: 'w-6 h-6',
  //   },
  //   {
  //     icon: 'images/del-icon.png',
  //     class: 'delete',
  //     onClick: (row) => this.deleteClient(row),
  //     iconSize: 'w-6 h-6',
  //   },
  // ];
  /*
  * Show edit/delete icons for RO only when the 
  * onboarding is in an editable stage; show only the 
  * view icon for all other `stsKey` values.
  */
  private readonly editableStatusCodes = [699, 701, 720];

  private isEditableStatus(status: number | null | undefined): boolean {
    const statusKey = Number(status);

    if (Number.isNaN(statusKey)) return false;

    return this.editableStatusCodes.includes(statusKey);
  }

  get isRelationshipOfficer(): boolean {
    return (
      typeof localStorage !== 'undefined' &&
      (localStorage.getItem('user_role') || '').trim().toUpperCase() === 'LNLY_RO'
    );
  }
private setActions(): void {
  const viewAction: TableAction = {
    icon: 'images/view-more.png',
    class: 'view',
    tooltip: 'ONB_CLIENT_LIST.VIEW_CLIENT',
    onClick: (row) => this.viewClient(row),
    iconSize: 'w-6 h-6',
  };

  if (this.isRelationshipOfficer) {
    this.actions = [
      {
        icon: 'images/edit-icon.png',
        class: 'edit',
        tooltip: 'ONB_CLIENT_LIST.EDIT_CLIENT',
        onClick: (row) => this.editClient(row),
        iconSize: 'w-6 h-6',

        visible: (row: Client) => this.isEditableStatus(row.status),
      },
      viewAction,
    ];

    return;
  }

  this.actions = [viewAction];
}

  openClientActionPopup(client: Client): void {
    if (!client?.id) {
      this.toastr.error('ONB_CLIENT_LIST.CLIENT_ID_MISSING');
      return;
    }

    this.pendingActionClient = client;
    this.selectedClientAction = 'sendBackToRO';
    this.showClientActionPopup = true;
  }

  closeClientActionPopup(): void {
    this.showClientActionPopup = false;
    this.pendingActionClient = null;
    this.selectedClientAction = 'sendBackToRO';
    this.isClientActionSubmitting = false;
  }

  submitClientAction(): void {
    if (!this.pendingActionClient || this.isClientActionSubmitting) {
      return;
    }

    const client = this.pendingActionClient;

    if (this.selectedClientAction === 'sendBackToRO' && !client.onboardingSeq) {
      this.toastr.error('ONB_CLIENT_LIST.ONBOARDING_SEQ_MISSING');
      return;
    }

    this.isClientActionSubmitting = true;

    const request$ =
      this.selectedClientAction === 'reject'
        ? this.onboarding.rejectOnboarding(client.id)
        : this.onboarding.sendBackOnboardingToRO(client.onboardingSeq!);

    request$.subscribe({
      next: (res: any) => {
        if (res?.__hasError || res?.error) {
          this.toastr.error(res?.error || res?.message || 'ONB_CLIENT_LIST.UNABLE_TO_PROCESS_CLIENT');
          this.isClientActionSubmitting = false;
          return;
        }

        this.toastr.success(
          this.selectedClientAction === 'reject'
            ? 'ONB_CLIENT_LIST.CLIENT_REJECTED_SUCCESS'
            : 'ONB_CLIENT_LIST.CLIENT_SENT_BACK_SUCCESS',
        );

        this.loadClients();
        this.closeClientActionPopup();
      },

      error: (err) => {
        this.toastr.error(err?.error?.error || err?.error?.message || 'ONB_CLIENT_LIST.UNABLE_TO_PROCESS_CLIENT');

        this.isClientActionSubmitting = false;
      },
    });
  }
  // ================= ACTION METHODS =================
  get canReviewClients(): boolean {
    const role = (localStorage.getItem('user_role') || '').trim().toUpperCase();

    return ['LNLY_CM', 'LNLY_CO', 'LNLY_TL', 'LNLY_VCM', 'LNLY_VPF', 'LNLY_SA'].includes(role);
  }

  viewClient(row: Client): void {
    this.router.navigate(['/onboarding/corporate', row.id], {
      queryParams: { clientId: row.id, mode: 'view' },
    });
  }

editClient(row: Client): void {
  if (!this.isRelationshipOfficer) return;

  if (!this.isEditableStatus(row.status)) {
    this.toastr.error('ONB_CLIENT_LIST.RECORD_NOT_EDITABLE');
    return;
  }

  this.router.navigate(['/onboarding/corporate', row.id], {
    queryParams: { clientId: row.id },
  });
}
  openExposure(row: Client): void {
    if (!this.isRelationshipOfficer) return;

    console.log('Exposure', row);
  }
}
